/**
 * SearchResults Page
 *
 * Displays global search results across CaseFiles, documents, and deadlines.
 * Client-side MVP implementation using existing list endpoints with filtering.
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Sidebar } from '../components/dashboard';
import {
  Search,
  FileText,
  FolderOpen,
  Clock,
  ArrowLeft,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import {
  searchDocuments,
  parseHighlightedSnippet,
} from '../utils/documentSearch';
import { useSemanticSearch, SemanticSearchResult } from '../hooks/useSemanticSearch';

// Hard cap on how many CaseFiles the document/deadline fan-out hits.
// Tracked separately as BUG-108; surfaced in the UI via SearchScope.
const MAX_CASEFILES_PER_GLOBAL_SEARCH = 5;

// Types for API responses
interface CaseFileSummary {
  caseFileId: string;
  name: string;
  dateOfBirth: string;
  diagnoses: string[];
  isOwner: boolean;
}

interface DocumentSummary {
  documentId: string;
  fileName: string;
  documentType?: string;
  category?: string;
  summary?: string;
  tags?: string[];
  extractedText?: string;
  extractedData?: Record<string, unknown>;
  searchableText?: string;
  caseFileId: string;
  caseFileName?: string;
}

interface DeadlineSummary {
  deadlineId: string;
  title: string;
  dueDate: string;
  status: string;
  type: string;
  caseFileId: string;
  caseFileName?: string;
}

interface ListCaseFilesResponse {
  success: boolean;
  caseFiles: CaseFileSummary[];
  totalCount: number;
}

interface ListDocumentsResponse {
  success: boolean;
  documents: DocumentSummary[];
  totalCount: number;
}

interface ListDeadlinesResponse {
  success: boolean;
  deadlines: DeadlineSummary[];
  totalCount: number;
}

// Search result types
interface SearchResults {
  caseFiles: CaseFileSummary[];
  documents: Array<DocumentSummary & { caseFileName: string }>;
  deadlines: Array<DeadlineSummary & { caseFileName: string }>;
}

// Records how much of the corpus the document fan-out actually covered.
//
// Two ways coverage can be incomplete:
// 1. The fan-out caps at 5 CaseFiles (BUG-108): caseFilesAttempted < caseFilesTotal.
// 2. A per-CaseFile listDocuments call fails: caseFilesSearched < caseFilesAttempted.
//
// Both surfaced in the UI so a partial "no match" never reads as definitive.
interface SearchScope {
  caseFilesSearched: number;   // calls that returned successfully
  caseFilesAttempted: number;  // calls actually issued (capped at MAX_CASEFILES_PER_GLOBAL_SEARCH)
  caseFilesFailed: number;     // attempted - searched
  caseFilesTotal: number;      // total CaseFiles the user has access to
}

/** Phase A document enriched with Phase B snippets when both fire. */
interface MergedDocumentResult extends DocumentSummary {
  caseFileName: string;
  snippets?: string[];
  relevanceScore?: number;
  /** Provenance — drives the "Also matching" label and the sparkles icon. */
  source: 'phaseA' | 'phaseB';
}

export function SearchResults(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';

  // TASK-022 Phase B: semantic search runs in parallel with Phase A. Behind a
  // flag (VITE_FEATURE_SEMANTIC_SEARCH); when off the hook returns disabled
  // and the page falls back to the original keyword behavior unchanged.
  const semantic = useSemanticSearch({ query, limit: 50 });

  const [results, setResults] = useState<SearchResults>({
    caseFiles: [],
    documents: [],
    deadlines: [],
  });
  // All CaseFile IDs → display names the caller has access to, regardless of
  // whether they keyword-match the current query. Used to resolve names for
  // semantic-only document hits whose CaseFile isn't in `results.caseFiles`.
  const [allCaseFileNames, setAllCaseFileNames] = useState<Map<string, string>>(
    new Map()
  );
  const [scope, setScope] = useState<SearchScope>({
    caseFilesSearched: 0,
    caseFilesAttempted: 0,
    caseFilesFailed: 0,
    caseFilesTotal: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(query);

  // Perform search when query changes
  useEffect(() => {
    if (!query.trim()) {
      setResults({ caseFiles: [], documents: [], deadlines: [] });
      setScope({ caseFilesSearched: 0, caseFilesAttempted: 0, caseFilesFailed: 0, caseFilesTotal: 0 });
      setLoading(false);
      return;
    }

    performSearch(query);
  }, [query]);

  const performSearch = async (searchQuery: string) => {
    setLoading(true);
    setError(null);

    const lowerQuery = searchQuery.toLowerCase();

    try {
      // Fetch all data in parallel
      const [caseFilesResult, documentsResults, deadlinesResults] = await Promise.all([
        fetchCaseFiles(),
        fetchAllDocuments(),
        fetchAllDeadlines(),
      ]);

      // Record how much of the corpus the document fan-out actually hit.
      // caseFilesSearched counts only successful listDocuments calls so we
      // never overstate coverage when a per-CaseFile call fails.
      setScope({
        caseFilesSearched: documentsResults.succeeded,
        caseFilesAttempted: documentsResults.attempted,
        caseFilesFailed: documentsResults.failed,
        caseFilesTotal: documentsResults.accessible,
      });

      // Filter CaseFiles by name or diagnoses
      const filteredCaseFiles = caseFilesResult.filter(
        (cf) =>
          cf.name.toLowerCase().includes(lowerQuery) ||
          cf.diagnoses.some((d) => d.toLowerCase().includes(lowerQuery))
      );

      // Create a map of caseFileId to name for enriching results. Also lift it
      // into component state so the semantic-merge useMemo can resolve names for
      // Phase B hits whose CaseFile isn't in `results.caseFiles` (the filtered
      // matched set).
      const caseFileMap = new Map<string, string>();
      caseFilesResult.forEach((cf) => caseFileMap.set(cf.caseFileId, cf.name));
      setAllCaseFileNames(caseFileMap);

      // Multi-field search across the AI-extracted content surfaced by
      // listDocuments (searchableText fast path covers fileName, documentType,
      // category, summary, tags, extractedText, and entity values).
      const filteredDocuments = searchDocuments(documentsResults.docs, searchQuery).map(
        (doc) => ({
          ...doc,
          caseFileName: caseFileMap.get(doc.caseFileId) || 'Unknown',
        })
      );

      // Filter deadlines by title
      const filteredDeadlines = deadlinesResults
        .filter((dl) => dl.title.toLowerCase().includes(lowerQuery))
        .map((dl) => ({
          ...dl,
          caseFileName: caseFileMap.get(dl.caseFileId) || 'Unknown',
        }));

      setResults({
        caseFiles: filteredCaseFiles,
        documents: filteredDocuments,
        deadlines: filteredDeadlines,
      });
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to perform search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseFiles = async (): Promise<CaseFileSummary[]> => {
    try {
      const listUserCaseFiles = httpsCallable<object, ListCaseFilesResponse>(
        functions,
        'listUserCaseFiles'
      );
      const result = await listUserCaseFiles({});
      return result.data.success ? result.data.caseFiles : [];
    } catch {
      return [];
    }
  };

  interface DocumentsFanOutResult {
    docs: DocumentSummary[];
    /** Number of listDocuments calls that returned a success response. */
    succeeded: number;
    /** Number of listDocuments calls that threw or returned success=false. */
    failed: number;
    /** Total listDocuments calls issued (capped at MAX_CASEFILES_PER_GLOBAL_SEARCH). */
    attempted: number;
    /** Total CaseFiles the user has access to, before the fan-out cap. */
    accessible: number;
  }

  const fetchAllDocuments = async (): Promise<DocumentsFanOutResult> => {
    // First get all CaseFiles, then fetch documents from each
    const caseFiles = await fetchCaseFiles();
    const allDocuments: DocumentSummary[] = [];

    const listDocuments = httpsCallable<
      { caseFileId: string; limit?: number },
      ListDocumentsResponse
    >(functions, 'listDocuments');

    // Fetch documents from each CaseFile (limit to first 5 for performance).
    // The 5-CaseFile cap is tracked separately as BUG-108; the scope indicator
    // below this section surfaces it to users so partial results aren't
    // mistaken for "no match anywhere."
    const caseFilesToSearch = caseFiles.slice(0, MAX_CASEFILES_PER_GLOBAL_SEARCH);

    // Promise.allSettled (not Promise.all) so a single CaseFile error doesn't
    // collapse the whole fan-out, and so we can count successes vs failures
    // for the scope indicator. Requesting limit: 500 makes each call
    // exhaustive within its CaseFile (backend caps at 500).
    const settled = await Promise.allSettled(
      caseFilesToSearch.map(async (cf) => {
        const result = await listDocuments({ caseFileId: cf.caseFileId, limit: 500 });
        if (!result.data.success) {
          throw new Error(`listDocuments returned success=false for ${cf.caseFileId}`);
        }
        return { cf, documents: result.data.documents };
      })
    );

    let succeeded = 0;
    let failed = 0;
    for (const outcome of settled) {
      if (outcome.status === 'fulfilled') {
        succeeded += 1;
        outcome.value.documents.forEach((doc) => {
          allDocuments.push({
            ...doc,
            caseFileId: outcome.value.cf.caseFileId,
            caseFileName: outcome.value.cf.name,
          });
        });
      } else {
        failed += 1;
        // Surface the failure for debugging; the UI will reflect it via
        // SearchScope so users see "could not be reached" instead of an
        // overstated "searched" count.
        console.warn('SearchResults: listDocuments fan-out failed for one CaseFile', outcome.reason);
      }
    }

    return {
      docs: allDocuments,
      succeeded,
      failed,
      attempted: caseFilesToSearch.length,
      accessible: caseFiles.length,
    };
  };

  const fetchAllDeadlines = async (): Promise<DeadlineSummary[]> => {
    // First get all CaseFiles, then fetch deadlines from each
    const caseFiles = await fetchCaseFiles();
    const allDeadlines: DeadlineSummary[] = [];

    const listDeadlines = httpsCallable<
      { caseFileId: string },
      ListDeadlinesResponse
    >(functions, 'listDeadlines');

    // Fetch deadlines from each CaseFile (limit to first 5 for performance)
    const caseFilesToSearch = caseFiles.slice(0, MAX_CASEFILES_PER_GLOBAL_SEARCH);

    // Deadline fan-out failures don't currently surface in the SearchScope
    // indicator (which tracks the document lane). Log them so a transient
    // listDeadlines outage isn't completely invisible.
    await Promise.all(
      caseFilesToSearch.map(async (cf) => {
        try {
          const result = await listDeadlines({ caseFileId: cf.caseFileId });
          if (result.data.success) {
            result.data.deadlines.forEach((dl) => {
              allDeadlines.push({
                ...dl,
                caseFileId: cf.caseFileId,
                caseFileName: cf.name,
              });
            });
          }
        } catch (err) {
          console.warn(
            `SearchResults: listDeadlines failed for CaseFile ${cf.caseFileId}`,
            err
          );
        }
      })
    );

    return allDeadlines;
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchInput.trim())}`);
    }
  };

  // TASK-022 Phase B: merge Phase A keyword results with Phase B semantic
  // results. Phase B (with snippets + ranking) takes precedence; Phase A
  // results not in Phase B get appended as "Also matching" so the keyword
  // fallback is never hidden when the semantic backend disagrees or is slow.
  const mergedDocuments: MergedDocumentResult[] = useMemo(() => {
    // Resolve CaseFile display names from the full accessible set, not just
    // the filtered-match set — semantic hits may live in CaseFiles that didn't
    // keyword-match the query.
    const phaseAById = new Map<string, typeof results.documents[number]>();
    for (const d of results.documents) phaseAById.set(d.documentId, d);

    const merged: MergedDocumentResult[] = [];
    const phaseBSeen = new Set<string>();

    if (semantic.enabled && semantic.status === 'success') {
      for (const r of semantic.results as SemanticSearchResult[]) {
        phaseBSeen.add(r.documentId);
        const fromPhaseA = phaseAById.get(r.documentId);
        merged.push({
          documentId: r.documentId,
          caseFileId: r.caseFileId,
          fileName: r.fileName || fromPhaseA?.fileName || '(untitled)',
          documentType: r.documentType || fromPhaseA?.documentType,
          category: r.category || fromPhaseA?.category,
          summary: fromPhaseA?.summary,
          tags: fromPhaseA?.tags,
          extractedText: fromPhaseA?.extractedText,
          extractedData: fromPhaseA?.extractedData,
          searchableText: fromPhaseA?.searchableText,
          caseFileName:
            allCaseFileNames.get(r.caseFileId) ||
            fromPhaseA?.caseFileName ||
            'Unknown',
          snippets: r.snippets,
          relevanceScore: r.relevanceScore,
          source: 'phaseB',
        });
      }
    }

    for (const d of results.documents) {
      if (phaseBSeen.has(d.documentId)) continue;
      merged.push({
        ...d,
        caseFileName:
          d.caseFileName || allCaseFileNames.get(d.caseFileId) || 'Unknown',
        source: 'phaseA',
      });
    }

    return merged;
  }, [
    results.documents,
    allCaseFileNames,
    semantic.enabled,
    semantic.status,
    semantic.results,
  ]);

  const totalResults =
    results.caseFiles.length +
    mergedDocuments.length +
    results.deadlines.length;

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar */}
      <div className="hidden lg:block">
        <Sidebar className="h-screen sticky top-0" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-soft-neutral px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate(-1)}
              aria-label="Go back"
              className="p-2 hover:bg-soft-neutral rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted" aria-hidden="true" />
            </button>
            <h1 className="text-xl font-semibold text-ink">Search Results</h1>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearchSubmit} className="max-w-2xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search records, documents, deadlines..."
                className="input pl-11 pr-4 py-3 text-base w-full"
                autoFocus
              />
            </div>
          </form>
        </header>

        {/* Content */}
        <main id="main-content" tabIndex={-1} className="flex-1 p-6 outline-none">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-2 border-evergreen border-t-transparent rounded-full mx-auto" />
                <p className="mt-4 text-muted">Searching...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center" role="alert">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" aria-hidden="true" />
                <p className="text-red-600">{error}</p>
                <button
                  onClick={() => performSearch(query)}
                  className="btn-secondary mt-4"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : !query.trim() ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-muted/50 mx-auto mb-4" />
              <p className="text-muted">Enter a search term to find records</p>
            </div>
          ) : totalResults === 0 ? (
            <div className="text-center py-12">
              <Search className="w-12 h-12 text-muted/50 mx-auto mb-4" />
              <p className="text-lg text-ink mb-2">No results found</p>
              <p className="text-muted">
                No matches for "{query}". Try a different search term.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Results Summary */}
              <p className="text-muted">
                Found {totalResults} result{totalResults !== 1 ? 's' : ''} for "
                {query}"
              </p>
              {(scope.caseFilesAttempted < scope.caseFilesTotal ||
                scope.caseFilesFailed > 0) && (
                <p className="text-xs text-muted -mt-6">
                  Document matches searched {scope.caseFilesSearched} of your{' '}
                  {scope.caseFilesTotal} care record
                  {scope.caseFilesTotal === 1 ? '' : 's'}
                  {scope.caseFilesFailed > 0 && (
                    <>
                      {' '}
                      ({scope.caseFilesFailed} could not be reached
                      {scope.caseFilesAttempted < scope.caseFilesTotal
                        ? `; ${scope.caseFilesTotal - scope.caseFilesAttempted} not searched`
                        : ''}
                      )
                    </>
                  )}
                  {scope.caseFilesFailed === 0 &&
                    scope.caseFilesAttempted < scope.caseFilesTotal && (
                      <> (most recent first; open a specific care record to search the rest)</>
                    )}
                  .
                </p>
              )}

              {/* CaseFiles Section */}
              {results.caseFiles.length > 0 && (
                <section>
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-ink mb-4">
                    <FolderOpen className="w-5 h-5 text-evergreen" />
                    CaseFiles ({results.caseFiles.length})
                  </h2>
                  <div className="grid gap-3">
                    {results.caseFiles.map((cf) => (
                      <button
                        key={cf.caseFileId}
                        onClick={() => navigate(`/casefile/${cf.caseFileId}`)}
                        className="card p-4 text-left hover:border-evergreen transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-evergreen/10 rounded-lg flex items-center justify-center">
                            <FolderOpen className="w-5 h-5 text-evergreen" />
                          </div>
                          <div>
                            <p className="font-medium text-ink">{cf.name}</p>
                            <p className="text-sm text-muted">
                              {cf.diagnoses.length > 0
                                ? cf.diagnoses.slice(0, 2).join(', ')
                                : 'No diagnoses listed'}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Documents Section */}
              {mergedDocuments.length > 0 && (
                <section>
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-ink mb-4">
                    <FileText className="w-5 h-5 text-brass" />
                    Documents ({mergedDocuments.length})
                    {semantic.enabled && semantic.status === 'loading' && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-muted">
                        <span className="animate-pulse">
                          <Sparkles className="w-3.5 h-3.5" />
                        </span>
                        searching deeper&hellip;
                      </span>
                    )}
                  </h2>
                  <div className="grid gap-3">
                    {mergedDocuments.map((doc, idx) => {
                      // First Phase A result after the last Phase B result becomes the
                      // "Also matching" divider.
                      const isFirstPhaseA =
                        doc.source === 'phaseA' &&
                        (idx === 0 || mergedDocuments[idx - 1].source === 'phaseB');
                      return (
                        <div key={doc.documentId}>
                          {isFirstPhaseA && idx > 0 && (
                            <p className="text-xs uppercase tracking-wide text-muted mt-2 mb-1">
                              Also matching
                            </p>
                          )}
                          <button
                            onClick={() =>
                              navigate(`/casefile/${doc.caseFileId}/documents`)
                            }
                            className="card p-4 text-left hover:border-brass transition-colors w-full"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-brass/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FileText className="w-5 h-5 text-brass" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-ink truncate flex items-center gap-2">
                                  {doc.fileName}
                                  {doc.source === 'phaseB' && (
                                    <Sparkles
                                      className="w-3.5 h-3.5 text-brass/70"
                                      aria-label="Semantic match"
                                    />
                                  )}
                                </p>
                                <p className="text-sm text-muted">
                                  {doc.caseFileName} {doc.category && `• ${doc.category}`}
                                </p>
                                {doc.snippets && doc.snippets.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {doc.snippets.slice(0, 2).map((snippet, sIdx) => (
                                      <p
                                        key={sIdx}
                                        className="text-xs text-muted leading-snug border-l-2 border-brass/30 pl-2"
                                      >
                                        {parseHighlightedSnippet(snippet).map((seg, segIdx) =>
                                          seg.highlighted ? (
                                            <mark
                                              key={segIdx}
                                              className="bg-brass/20 text-ink rounded px-0.5"
                                            >
                                              {seg.text}
                                            </mark>
                                          ) : (
                                            <span key={segIdx}>{seg.text}</span>
                                          )
                                        )}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Deadlines Section */}
              {results.deadlines.length > 0 && (
                <section>
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-ink mb-4">
                    <Clock className="w-5 h-5 text-red-500" />
                    Deadlines ({results.deadlines.length})
                  </h2>
                  <div className="grid gap-3">
                    {results.deadlines.map((dl) => (
                      <button
                        key={dl.deadlineId}
                        onClick={() =>
                          navigate(`/casefile/${dl.caseFileId}/deadlines`)
                        }
                        className="card p-4 text-left hover:border-red-300 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                            <Clock className="w-5 h-5 text-red-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-ink truncate">
                              {dl.title}
                            </p>
                            <p className="text-sm text-muted">
                              {dl.caseFileName} • {dl.type} •{' '}
                              <span
                                className={
                                  dl.status === 'overdue'
                                    ? 'text-red-500'
                                    : dl.status === 'upcoming'
                                    ? 'text-amber-500'
                                    : ''
                                }
                              >
                                {dl.status}
                              </span>
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default SearchResults;
